import { THEMES, isTheme, type Theme } from '@evogard/domain';
/**
 * Quem escreve em `data-tema`.
 *
 * O `theme.css` já sabe fazer tudo: `light-dark()` resolve claro e escuro da
 * mesma linha, `prefers-color-scheme` decide por omissão e `data-tema` tem a
 * palavra final. O que faltava era **alguém escrever ali** — o `CLAUDE.md`
 * registrava isso como pendência: "o `data-tema` já funciona e ninguém escreve
 * nele".
 *
 * Este arquivo é esse alguém, e é um só para as duas SPAs. Duas cópias — uma na
 * interna, outra no portal — divergiriam no primeiro ajuste, e o resultado
 * seria a mesma pessoa vendo temas diferentes nos dois lados da casa.
 *
 * ## Onde o tema mora
 *
 * Em dois lugares, e a diferença importa:
 *
 * - **No servidor**, na preferência da pessoa (`usuario_preferencias.tema` e
 *   `portal_associado_preferencias.tema`). É o que faz o tema seguir a conta
 *   entre dispositivos.
 * - **No `localStorage`**, como eco local. É o que evita o *flash*: a
 *   preferência do servidor chega uma requisição depois da primeira pintura, e
 *   sem o eco a tela pisca clara antes de escurecer.
 *
 * O servidor manda; o eco só existe para a primeira pintura.
 */

export { THEMES, isTheme, type Theme };

/** A chave do eco local. Uma só, e com prefixo: `localStorage` é global por origem. */
export const THEME_KEY = 'evogard:tema';

/*
 * `THEMES`, `Theme` e `isTheme` são os de `@evogard/domain`, reexportados: até
 * 02/09/2026 este arquivo tinha a própria cópia dos três porque o pacote não
 * dependia do domínio. O que fica aqui é só o efeito — escrever no documento.
 */

/**
 * Aplica o tema no documento.
 *
 * `sistema` **remove** o atributo em vez de escrever `data-tema="sistema"` —
 * é `prefers-color-scheme` que decide, e um atributo com valor que o CSS não
 * conhece deixaria a página presa no claro para quem usa o sistema escuro.
 */
export function applyTheme(theme: Theme, doc: Document = document): void {
  const root = doc.documentElement;
  if (theme === 'sistema') root.removeAttribute('data-tema');
  else root.setAttribute('data-tema', theme);
}

/**
 * Lê o eco local. `sistema` quando não há nada gravado — ou quando o
 * `localStorage` não está disponível.
 *
 * O `try` não é decoração: em janela anônima de alguns navegadores, e com
 * cookies de terceiros bloqueados em certos contextos, **ler** `localStorage`
 * lança. Sem o `try`, a aplicação inteira morre antes da primeira pintura por
 * causa de uma preferência de cor.
 */
export function readLocalTheme(): Theme {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return isTheme(saved) ? saved : 'sistema';
  } catch {
    return 'sistema';
  }
}

export function saveLocalTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // sem eco local o tema ainda funciona; só volta a piscar na primeira
    // pintura. Não é motivo para quebrar nada.
  }
}

/**
 * Chamado **antes da primeira pintura**, no `main.tsx`, com o que houver de eco.
 *
 * Precisa rodar antes de o React montar. Depois, a página já pintou clara e a
 * troca vira um flash — o defeito que faz uma interface escura parecer
 * malfeita mesmo estando certa.
 */
export function applySavedTheme(doc: Document = document): Theme {
  const theme = readLocalTheme();
  applyTheme(theme, doc);
  return theme;
}

/**
 * Grava a escolha e aplica, de uma vez.
 *
 * Quem persiste no servidor é a tela — ela conhece a rota da própria aplicação
 * (`/plataforma/preferencias` ou `/portal/preferencias`), e este pacote não
 * conhece nenhuma das duas.
 */
export function chooseTheme(theme: Theme, doc: Document = document): void {
  saveLocalTheme(theme);
  applyTheme(theme, doc);
}
